import translate from '@allesauto/i18n';
import { isAuregoShop } from '@allesauto/utils/funcs.ts';
import {
  SCREEN_BREAKPOINT,
  useCSR,
  useScreenBreakpoint,
} from '@allesauto/utils/hooks';
import { Close20, SortDescending16 } from '@carbon/icons-react';
import { Dialog, Transition } from '@headlessui/react';
import clsx from 'clsx';
import React, { FC, Fragment, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import OverlayList from './overlay/OverlayList';
import Button from '../button';
import Dropdown from '../dropdown';
import TextLink from '../textLink';
import { useWhiteLabel } from '../whiteLabel';

interface SortingItem {
  label: string;
  subject: string;
  direction: string;
}

export interface SortingProps {
  items: SortingItem[];
  defaultValue: SortingItem;
  disabled?: boolean;
  context?: string;
  onChange?: (value: any) => void;
  hasGreyBg?: boolean;
  whiteLabelIntegrationSlug?: string;
}

const Sorting: FC<SortingProps> = ({
  items,
  defaultValue,
  disabled,
  context,
  onChange,
  hasGreyBg = true,
  whiteLabelIntegrationSlug,
}: SortingProps) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(defaultValue);
  const whiteLabel = useWhiteLabel();

  useEffect(() => {
    setSelectedItem(defaultValue);
  }, [defaultValue]);

  const breakpoint = useScreenBreakpoint();

  const intl = useIntl();
  const csr = useCSR();

  const labelSM = translate(
    intl,
    'ui.PCP.HORIZONTAL_TOOLBAR.SORTING.SMALL_SCREEN_TEXT_LINK_LABEL'
  );
  const labelLG = translate(
    intl,
    'ui.PCP.HORIZONTAL_TOOLBAR.SORTING.LARGE_SCREEN_DROP_DOWN_LABEL'
  );

  const className = {
    iconClose:
      '!border-0 !shadow-none focus:!shadow-none !bg-transparent focus:!ring-opacity-0 !text-purple80 monochrome:!text-grey90 !h-[20px] !w-[20px] !p-0',
    overlayTitleContainer:
      'bg-white flex flex-row h-[50px] items-center justify-between p-[15px]',
    overlayTitleText:
      'font-regular font-weight-bold leading-5 mr-10px text-p1 text-purple80 monochrome:text-grey90',
    overlayOutsideContent: 'bg-grey100 bg-opacity-60 fixed inset-0',
    overlayContainer: 'fixed inset-0 overflow-y-hidden z-50',
    overlayContent:
      'bg-grey30 absolute flex-col transform transition-all w-full max-h-screen',
    overlay: clsx(
      'min-h-full',
      whiteLabelIntegrationSlug
        ? `theme-monochrome wl-${whiteLabelIntegrationSlug}`
        : undefined
    ),
  };

  const handleItemChange = (item: string | number | undefined) => {
    if (item) {
      const realItem = items.find((it) => it.label == item);
      if (realItem) setSelectedItem(realItem);
      onChange?.(realItem);
    }
    setIsOverlayOpen(false);
  };

  if (!csr) {
    return (
      <div className="animate-pulse bg-grey20 h-[50px] rounded-xl w-[100px] lg:w-[180px]" />
    );
  }

  const parsedItems = items.map((item) => item.label);

  return (
    <>
      {breakpoint < SCREEN_BREAKPOINT.LG ? (
        <>
          <div className="flex items-center">
            <TextLink
              disabled={disabled}
              text={labelSM}
              customClassName={`${
                isAuregoShop(whiteLabel) && '!text-black tablet-filter'
              }`}
              onClick={() => {
                setIsOverlayOpen(!isOverlayOpen);
              }}
              icon={<SortDescending16 />}
              iconPosition={'right'}
            />
          </div>

          <Transition appear show={isOverlayOpen} as={Fragment}>
            <Dialog
              as="div"
              className={className.overlayContainer}
              onClose={() => {
                setIsOverlayOpen(!isOverlayOpen);
              }}
            >
              <div
                id="alles-auto-overlay-content"
                className={className.overlay}
              >
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Dialog.Overlay className={className.overlayOutsideContent} />
                </Transition.Child>

                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <div className={className.overlayContent}>
                    <Dialog.Title>
                      <div className={className.overlayTitleContainer}>
                        <div className={className.overlayTitleText}>
                          {labelSM}
                        </div>
                        <Button
                          icon={<Close20 aria-hidden="true" />}
                          customClassName={className.iconClose}
                          onClick={() => {
                            setIsOverlayOpen(!isOverlayOpen);
                          }}
                        />
                      </div>
                    </Dialog.Title>
                    <OverlayList
                      items={parsedItems}
                      selectedItem={selectedItem.label}
                      onItemClick={(item) => handleItemChange(item)}
                    />
                  </div>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>
        </>
      ) : (
        <Dropdown
          items={parsedItems}
          label={labelLG}
          value={selectedItem.label}
          disabled={disabled}
          labelPosition="left"
          onChange={({ value }) => handleItemChange(value)}
          hasGreyBg={hasGreyBg}
          context={context === 'horizontal-toolbar' ? 'horizontal-toolbar' : ''}
          whiteLabel={!!whiteLabel}
        />
      )}
    </>
  );
};

export default Sorting;
